import React, { ReactNode } from 'react';
import classnames from 'classnames';
import { useSystem } from '@ohif/core';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icons,
  useIconPresentation,
  useViewportGrid,
} from '@ohif/ui-next';
import { useDownloadExportStore } from '../../stores/useDownloadExportStore';
import ViewportDownloadMenu from './ViewportDownloadMenu';

type ViewportDownloadMenuWrapperProps = withAppTypes<{
  viewportId?: string;
  location?: number;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  disabled?: boolean;
}>;

function ViewportDownloadMenuWrapper({
  viewportId,
  location,
  isOpen = false,
  onOpen,
  onClose,
  disabled,
  ...rest
}: ViewportDownloadMenuWrapperProps): ReactNode {
  const { servicesManager } = useSystem();
  const { toolbarService } = servicesManager.services;
  const [gridState] = useViewportGrid();
  const viewportIdToUse = viewportId || gridState.activeViewportId;
  const { IconContainer, className: iconClassName, containerProps } = useIconPresentation();

  const isExporting = useDownloadExportStore(state => state.isExporting);

  const { align, side } = toolbarService.getAlignAndSide(location);

  const handleOpenChange = (openState: boolean) => {
    if (isExporting && !openState) {
      return;
    }

    if (openState) {
      onOpen?.();
      return;
    }

    onClose?.();
  };

  const triggerDisabled = disabled || isExporting;
  const icon = isExporting ? (
    <Icons.LoadingSpinner className={classnames(iconClassName, 'animate-spin')} />
  ) : (
    <Icons.Download className={iconClassName} />
  );

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <DropdownMenuTrigger
        asChild
        className="flex items-center justify-center"
        disabled={triggerDisabled}
      >
        <div data-cy="viewport-download-menu-trigger">
          {IconContainer ? (
            <IconContainer
              disabled={triggerDisabled}
              {...rest}
              {...containerProps}
            >
              {icon}
            </IconContainer>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              disabled={triggerDisabled}
              aria-label={isExporting ? 'Download in progress' : 'Download'}
              aria-busy={isExporting}
            >
              {icon}
            </Button>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        side={side}
        sideOffset={5}
        className="viewport-download-menu min-w-[12rem] w-max max-w-[min(20rem,calc(100vw-2rem))] p-1"
        data-cy="viewport-download-menu"
        onCloseAutoFocus={event => {
          if (isExporting) {
            event.preventDefault();
          }
        }}
      >
        {viewportIdToUse ? (
          <ViewportDownloadMenu
            viewportId={viewportIdToUse}
            onActionComplete={onClose}
            onExportComplete={onClose}
          />
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ViewportDownloadMenuWrapper;
